import { useCallback, useEffect, useRef, useState } from 'react';

import { extractErrorMessage } from '../api/errors';
import type { ReportStatus } from '../api/reports';
import { useInsightsPolling } from './useInsightsPolling';

/**
 * Save a presigned file without opening a window. The API signs report and
 * export links with `Content-Disposition: attachment`, so following one
 * downloads in place — and unlike `window.open`, which the browser blocks
 * once the click is a few seconds old, a download is never a pop-up.
 */
export function saveFile(url: string, filename?: string | null): void {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Request → poll → save for a file the worker builds on demand (session and
 * longitudinal PDF reports, data exports). `A` is whatever picks the file —
 * nothing for a report, the format for an export.
 *
 * Every outcome is said out loud, because the silent version is what made
 * people unsure a report had been made at all: `done` names the saved file,
 * `error` carries a failure, and a render that outlives the poller clears
 * the spinner with a "still preparing" note instead of spinning forever.
 */
export function usePreparedDownload<A = void>(opts: {
  fetchStatus: (arg: A) => Promise<ReportStatus>;
  request: (arg: A) => Promise<void>;
  /** "report", "export" — used in the messages. */
  noun: string;
  /** Hand over a file already built for this argument instead of building
   *  a new one (default). Off for data exports: their contents change as
   *  sessions come in, so reusing the last build returned stale data. */
  reuseExisting?: boolean;
}): {
  start: (arg: A) => Promise<void>;
  /** The argument being prepared, or null when idle. */
  pending: { arg: A } | null;
  error: string | null;
  /** Filename of the last file handed to the browser. */
  done: string | null;
  reset: () => void;
} {
  const { fetchStatus, request, noun, reuseExisting = true } = opts;
  const [pending, setPending] = useState<{ arg: A } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const deliver = (r: ReportStatus) => {
    saveFile(r.url as string, r.filename);
    setDone(r.filename ?? `your ${noun}`);
  };

  const reset = useCallback(() => {
    setPending(null);
    setError(null);
    setDone(null);
  }, []);

  // Set synchronously: `pending` only lands after two awaits, so without
  // this a quick second click started a second build (review DL-1).
  const startingRef = useRef(false);

  const start = async (arg: A) => {
    if (startingRef.current || pending !== null) return;
    startingRef.current = true;
    setError(null);
    setDone(null);
    try {
      // A prior build is reusable (deterministic S3 key) — hand it over now.
      if (reuseExisting) {
        const existing = await fetchStatus(arg);
        if (existing.status === 'ready' && existing.url) {
          deliver(existing);
          return;
        }
      }
      await request(arg);
      setPending({ arg });
    } catch (e) {
      setError(extractErrorMessage(e, `could not start the ${noun}`));
    } finally {
      startingRef.current = false;
    }
  };

  const { polling, attempts } = useInsightsPolling({
    pending: pending !== null,
    onTick: () => {
      if (pending === null) return;
      fetchStatus(pending.arg)
        .then((r) => {
          if (r.status === 'ready' && r.url) {
            setPending(null);
            deliver(r);
          } else if (r.status === 'failed') {
            setPending(null);
            setError(`${noun} generation failed — try again`);
          }
        })
        .catch(() => {});
    },
  });

  // The poller gives up after its budget; without this the button would sit
  // on "Preparing…" for good. The build may still land — asking again picks
  // it up instantly from the reusable key.
  useEffect(() => {
    if (pending !== null && !polling && attempts > 0) {
      setPending(null);
      setError(`the ${noun} is taking longer than usual — try again in a minute`);
    }
  }, [pending, polling, attempts, noun]);

  return { start, pending, error, done, reset };
}
